// Transport routes (taxgw-lcaj, docs/ART-DIRECTION.md §1, §2.4): every own transport craft's trip as a dashed
// spherical line in the owner's faction colour, the dashes flowing toward the destination, just above the war network.
// The sim sends a craft's route on every trip change (SimEvent craftRoute, owner only): flat lat/lon control points —
// a plane's great circle (start, destination) or a boat's water path — empty when the craft has no trip. The layer
// keeps the latest route per craft in a slot store presized for the war's transport capacity (match/warSetup.ts) ×
// the route point cap. Each frame TransportRouteSystem draws the route of every craft in the mirror, trimmed at its
// render pose: the line starts at the craft and runs along the rest of the route, every leg tessellated along its
// great circle (ROUTE_STEP_RAD, the shared route policy). A route whose craft is no longer in the mirror is dropped at
// the frame's end. A frame writes begin() → draw()… → end() without allocating; the store and the line buffers grow
// (growth.ts) only when the war outgrows its presize.

import { AdditiveBlending, BufferAttribute, BufferGeometry, DynamicDrawUsage, LineSegments, ShaderMaterial, Vector3, type Color } from 'three'
import { InvariantViolation, MAX_CRAFT_ROUTE_POINTS, type OwnerValue, type PlayerId, type SimEvent, type SimId } from '@taxwar/net'
import type { WarCapacity } from '../../match/warSetup'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { ROUTE_STEP_RAD } from '../geo/routeTessellation'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { factionRole, type Palette } from '../palette'
import { uploadPrefix } from '../units/LineBatch'
import { grownCapacity, grownVertices } from '../units/growth'
import { ROUTE_FRAGMENT, ROUTE_VERTEX } from './routeShaders'

const MAX_LATITUDE_DEG = 90
/** Routes run just above the war network, under the lock-on lines (ART-DIRECTION §2.4: never under geography). */
const ROUTE_ALTITUDE = GLOBE_ALTITUDE.network + 0.002
/** Dimmer than lock-on lines: a trip is intent, not gameplay-critical feedback (ART-DIRECTION §2.5). */
const ROUTE_INTENSITY = 1
/** Dash period along a route in radians of great-circle arc (render units on the unit globe). */
const ROUTE_DASH_PERIOD = 0.02
const ROUTE_DASH_DUTY = 0.55
/** Dash cycles per second: any pixel on a route toggles at this rate, kept under 3 Hz (ART-DIRECTION §4). */
const ROUTE_DASH_CYCLES_PER_SEC = 0.75
/** Dash flow per reduce-effects setting: "Reduce effects" stops scrolling motion (ART-DIRECTION §4). */
const DASH_FLOW = { full: 1, reduced: 0 } as const
/**
 * Below this angle a leg has no length (a repeated control point): the craft is past it at once and it draws nothing.
 * The same bound on a leg's sine with its ends facing apart marks antipodal ends, which span no single great circle.
 */
const MIN_ARC_RAD = 1e-9
const LAT_LON_COMPONENTS = 2
const POSITION_COMPONENTS = 3
const COLOUR_COMPONENTS = 3
const SEGMENT_VERTICES = 2

export class TransportRouteLayer {
  readonly root: LineSegments

  private readonly material: ShaderMaterial
  private readonly dashPhase = { value: 0 }
  private dashFlow: number
  private palette: Palette

  // Line buffers, rebuilt every frame.
  private positions: BufferAttribute
  private colours: BufferAttribute
  private distances: BufferAttribute
  private segmentCapacity: number
  private segmentCount = 0

  // Route store: one slot per craft with a trip.
  /** Craft SimId → its route's slot. */
  private readonly slotOf = new Map<SimId, number>()
  private slotCapacity: number
  /** Per slot × MAX_CRAFT_ROUTE_POINTS: control points as globe-space unit vectors (xyz). */
  private points: Float32Array
  /** Per slot × MAX_CRAFT_ROUTE_POINTS: great-circle distance from the route's first point (radians). */
  private along: Float32Array
  /** Per slot: control point count (>= 2 while the slot holds a route). */
  private pointCounts: Int32Array
  /** Per slot: the leg the craft is on. It only moves forward, so a route that doubles back trims in order. */
  private legs: Int32Array
  /** Per slot: the craft holding it. */
  private crafts: Float64Array
  /** Per slot: 1 once drawn this frame (its craft is in the mirror). */
  private seen: Uint8Array
  /** Per slot: its index in `active`. */
  private activeIndex: Int32Array
  /** The slots holding a route (the first `activeCount`). */
  private active: Int32Array
  private activeCount = 0
  /** Free slots, a stack of `freeCount`. */
  private free: Int32Array
  private freeCount = 0

  private readonly craftAt = new Vector3()
  private readonly legStart = new Vector3()
  private readonly legEnd = new Vector3()
  private readonly across = new Vector3()
  private readonly vertexFrom = new Vector3()
  private readonly vertexTo = new Vector3()

  constructor(
    private readonly viewer: PlayerId,
    capacity: WarCapacity,
    palette: Palette,
    reduceEffects: boolean,
  ) {
    this.palette = palette
    this.dashFlow = dashFlowOf(reduceEffects)

    this.slotCapacity = capacity.transports
    this.points = new Float32Array(this.slotCapacity * MAX_CRAFT_ROUTE_POINTS * POSITION_COMPONENTS)
    this.along = new Float32Array(this.slotCapacity * MAX_CRAFT_ROUTE_POINTS)
    this.pointCounts = new Int32Array(this.slotCapacity)
    this.legs = new Int32Array(this.slotCapacity)
    this.crafts = new Float64Array(this.slotCapacity)
    this.seen = new Uint8Array(this.slotCapacity)
    this.activeIndex = new Int32Array(this.slotCapacity)
    this.active = new Int32Array(this.slotCapacity)
    this.free = new Int32Array(this.slotCapacity)
    this.pushFreeSlots(0, this.slotCapacity)

    // A route has at most MAX_CRAFT_ROUTE_POINTS − 1 legs plus the lead-in from the craft; long legs tessellate into
    // more segments, so this is a presize the buffers grow past, not a bound.
    this.segmentCapacity = capacity.transports * MAX_CRAFT_ROUTE_POINTS
    const vertices = this.segmentCapacity * SEGMENT_VERTICES
    this.positions = new BufferAttribute(new Float32Array(vertices * POSITION_COMPONENTS), POSITION_COMPONENTS).setUsage(DynamicDrawUsage)
    this.colours = new BufferAttribute(new Float32Array(vertices * COLOUR_COMPONENTS), COLOUR_COMPONENTS).setUsage(DynamicDrawUsage)
    this.distances = new BufferAttribute(new Float32Array(vertices), 1).setUsage(DynamicDrawUsage)
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', this.positions)
    geometry.setAttribute('aColor', this.colours)
    geometry.setAttribute('aDistance', this.distances)
    geometry.setDrawRange(0, 0)
    this.material = new ShaderMaterial({
      uniforms: {
        uIntensity: { value: ROUTE_INTENSITY },
        uDashPeriod: { value: ROUTE_DASH_PERIOD },
        uDashPhase: this.dashPhase,
        uDashDuty: { value: ROUTE_DASH_DUTY },
      },
      vertexShader: ROUTE_VERTEX,
      fragmentShader: ROUTE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.root = new LineSegments(geometry, this.material)
    // Routes change every frame; a cached bounding sphere would cull them wrongly.
    this.root.frustumCulled = false
    assignLayer(this.root, EMISSIVE_LAYER)
  }

  /** Match.onEvents listener: keeps every craftRoute (its points replace the craft's route; empty removes it). */
  readonly applyEvents = (events: readonly SimEvent[]): void => {
    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SimEvent
      if (event.kind === 'craftRoute') this.setRoute(event.craft, event.points)
    }
  }

  begin(): void {
    this.segmentCount = 0
  }

  /**
   * Draws `craft`'s route, if it has one, trimmed at its render pose (degrees). The craft moves on to the next leg
   * once it is past the end of its current one; the line starts at the craft, toward its leg's end, or toward the
   * leg's start while the craft has not reached it (a route's first point can lie just ahead of the craft).
   */
  draw(craft: SimId, owner: OwnerValue, latDeg: number, lonDeg: number): void {
    const slot = this.slotOf.get(craft)
    if (slot === undefined) return
    this.seen[slot] = 1
    const colour = this.palette[factionRole(owner, this.viewer)]
    const base = slot * MAX_CRAFT_ROUTE_POINTS
    const lastLeg = (this.pointCounts[slot] as number) - 2
    const craftAt = latLonToVector3(latDeg, lonDeg, 1, this.craftAt)

    let leg = this.legs[slot] as number
    let progress = this.legProgress(base + leg, craftAt)
    while (progress >= 1 && leg < lastLeg) {
      leg++
      progress = this.legProgress(base + leg, craftAt)
    }
    this.legs[slot] = leg

    const legFrom = base + leg
    const legFromAlong = this.along[legFrom] as number
    let next: number
    let craftAlong: number
    if (progress > 0) {
      next = legFrom + 1
      craftAlong = legFromAlong + Math.min(1, progress) * ((this.along[next] as number) - legFromAlong)
    } else {
      next = legFrom
      craftAlong = legFromAlong - arcBetween(craftAt, this.loadPoint(legFrom, this.legStart), this.across)
    }
    const routeEnd = base + lastLeg + 1
    this.pushArc(craftAt, craftAlong, this.loadPoint(next, this.legEnd), this.along[next] as number, colour)
    for (let point = next; point < routeEnd; point++) {
      this.loadPoint(point, this.legStart)
      this.loadPoint(point + 1, this.legEnd)
      this.pushArc(this.legStart, this.along[point] as number, this.legEnd, this.along[point + 1] as number, colour)
    }
  }

  /** Uploads only the written segments and drops every route whose craft was not drawn this frame (left the mirror). */
  end(): void {
    // Backward, so a swap-remove only moves an already visited slot into the current index.
    for (let i = this.activeCount - 1; i >= 0; i--) {
      const slot = this.active[i] as number
      if (this.seen[slot] === 1) this.seen[slot] = 0
      else this.releaseSlot(slot)
    }
    const vertices = this.segmentCount * SEGMENT_VERTICES
    this.root.geometry.setDrawRange(0, vertices)
    if (vertices === 0) return
    uploadPrefix(this.positions, vertices * POSITION_COMPONENTS)
    uploadPrefix(this.colours, vertices * COLOUR_COMPONENTS)
    uploadPrefix(this.distances, vertices)
  }

  /** Once per frame: scrolls the dashes toward the destinations (frozen with reduced effects). */
  update(dtSeconds: number): void {
    this.dashPhase.value = (this.dashPhase.value + dtSeconds * ROUTE_DASH_CYCLES_PER_SEC * this.dashFlow) % 1
  }

  setReduceEffects(reduceEffects: boolean): void {
    this.dashFlow = dashFlowOf(reduceEffects)
  }

  /** Lines are rebuilt every frame, so the next frame shows the new palette. */
  setPalette(palette: Palette): void {
    this.palette = palette
  }

  dispose(): void {
    this.root.removeFromParent()
    this.root.geometry.dispose()
    this.material.dispose()
  }

  /** Validates `points` (the room's event is a boundary) and stores them as `craft`'s route; empty removes it. */
  private setRoute(craft: SimId, points: readonly number[]): void {
    if (points.length % LAT_LON_COMPONENTS !== 0) throw new InvariantViolation(`craft ${craft} route has an odd coordinate count ${points.length}`)
    const count = points.length / LAT_LON_COMPONENTS
    if (count === 0) {
      const slot = this.slotOf.get(craft)
      if (slot !== undefined) this.releaseSlot(slot)
      return
    }
    if (count < 2 || count > MAX_CRAFT_ROUTE_POINTS) {
      throw new InvariantViolation(`craft ${craft} route has ${count} points; a route has 2 to ${MAX_CRAFT_ROUTE_POINTS}`)
    }
    for (let k = 0; k < points.length; k += LAT_LON_COMPONENTS) {
      const lat = points[k] as number
      const lon = points[k + 1] as number
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > MAX_LATITUDE_DEG) {
        throw new InvariantViolation(`craft ${craft} route point ${k / LAT_LON_COMPONENTS} (${lat}, ${lon}) is not on the globe`)
      }
    }

    let slot = this.slotOf.get(craft)
    if (slot === undefined) slot = this.claimSlot(craft)
    const base = slot * MAX_CRAFT_ROUTE_POINTS
    const previous = this.legStart
    const current = this.legEnd
    let along = 0
    for (let k = 0; k < count; k++) {
      latLonToVector3(points[k * LAT_LON_COMPONENTS] as number, points[k * LAT_LON_COMPONENTS + 1] as number, 1, current)
      if (k > 0) {
        if (previous.dot(current) < 0 && this.across.crossVectors(previous, current).length() < MIN_ARC_RAD) {
          throw new InvariantViolation(`craft ${craft} route leg ${k - 1} joins antipodal points; no single great circle spans it`)
        }
        along += arcBetween(previous, current, this.across)
      }
      current.toArray(this.points, (base + k) * POSITION_COMPONENTS)
      this.along[base + k] = along
      previous.copy(current)
    }
    this.pointCounts[slot] = count
    this.legs[slot] = 0
  }

  private claimSlot(craft: SimId): number {
    if (this.freeCount === 0) this.growStore()
    this.freeCount--
    const slot = this.free[this.freeCount] as number
    this.slotOf.set(craft, slot)
    this.crafts[slot] = craft
    this.seen[slot] = 0
    this.activeIndex[slot] = this.activeCount
    this.active[this.activeCount] = slot
    this.activeCount++
    return slot
  }

  private releaseSlot(slot: number): void {
    this.slotOf.delete(this.crafts[slot] as SimId)
    this.pointCounts[slot] = 0
    this.seen[slot] = 0
    const index = this.activeIndex[slot] as number
    this.activeCount--
    const moved = this.active[this.activeCount] as number
    this.active[index] = moved
    this.activeIndex[moved] = index
    this.free[this.freeCount] = slot
    this.freeCount++
  }

  /** Pushes slots [from, to) so the lowest is claimed first. */
  private pushFreeSlots(from: number, to: number): void {
    for (let slot = to - 1; slot >= from; slot--) {
      this.free[this.freeCount] = slot
      this.freeCount++
    }
  }

  /** Doubles the route store, keeping every stored route (only when the war holds more trips than its presize). */
  private growStore(): void {
    const previous = this.slotCapacity
    const capacity = grownCapacity(previous, previous + 1)
    this.points = grownArray(this.points, capacity * MAX_CRAFT_ROUTE_POINTS * POSITION_COMPONENTS)
    this.along = grownArray(this.along, capacity * MAX_CRAFT_ROUTE_POINTS)
    this.pointCounts = grownArray(this.pointCounts, capacity)
    this.legs = grownArray(this.legs, capacity)
    this.crafts = grownArray(this.crafts, capacity)
    this.seen = grownArray(this.seen, capacity)
    this.activeIndex = grownArray(this.activeIndex, capacity)
    this.active = grownArray(this.active, capacity)
    this.free = grownArray(this.free, capacity)
    this.slotCapacity = capacity
    this.pushFreeSlots(previous, capacity)
  }

  /** Writes control point `index` (store-wide) into `out`. */
  private loadPoint(index: number, out: Vector3): Vector3 {
    return out.fromArray(this.points, index * POSITION_COMPONENTS)
  }

  /**
   * How far along the leg starting at control point `index` (store-wide) the craft is: the angle of its projection
   * onto the leg's great circle from the leg's start, over the leg's angle (< 0 before the start, >= 1 past the end).
   * A leg without length is passed at once.
   */
  private legProgress(index: number, craftAt: Vector3): number {
    const start = this.loadPoint(index, this.legStart)
    const end = this.loadPoint(index + 1, this.legEnd)
    const angle = arcBetween(start, end, this.across)
    if (angle < MIN_ARC_RAD) return 1
    // The leg's great circle: `start` and the unit direction toward `end` perpendicular to it.
    const toward = this.across.copy(end).addScaledVector(start, -start.dot(end)).normalize()
    return Math.atan2(craftAt.dot(toward), craftAt.dot(start)) / angle
  }

  /**
   * Pushes the great-circle arc from `from` to `to` (unit vectors) as line segments of at most ROUTE_STEP_RAD, the
   * route distance interpolated from `fromAlong` to `toAlong`. Clobbers the vertex scratch vectors only.
   */
  private pushArc(from: Vector3, fromAlong: number, to: Vector3, toAlong: number, colour: Color): void {
    const angle = arcBetween(from, to, this.vertexTo)
    if (angle < MIN_ARC_RAD) return
    const sinAngle = Math.sin(angle)
    if (sinAngle < MIN_ARC_RAD) throw new InvariantViolation('route arc joins antipodal points; no single great circle spans it')
    const steps = Math.ceil(angle / ROUTE_STEP_RAD)
    const vertexFrom = this.vertexFrom.copy(from).multiplyScalar(ROUTE_ALTITUDE)
    let alongFrom = fromAlong
    for (let s = 1; s <= steps; s++) {
      const f = s / steps
      const weightFrom = (Math.sin((1 - f) * angle) / sinAngle) * ROUTE_ALTITUDE
      const weightTo = (Math.sin(f * angle) / sinAngle) * ROUTE_ALTITUDE
      const vertexTo = this.vertexTo.copy(from).multiplyScalar(weightFrom).addScaledVector(to, weightTo)
      const alongTo = fromAlong + (toAlong - fromAlong) * f
      this.pushSegment(vertexFrom, alongFrom, vertexTo, alongTo, colour)
      vertexFrom.copy(vertexTo)
      alongFrom = alongTo
    }
  }

  private pushSegment(from: Vector3, fromAlong: number, to: Vector3, toAlong: number, colour: Color): void {
    if (this.segmentCount >= this.segmentCapacity) this.growLines(this.segmentCount + 1)
    const vertex = this.segmentCount * SEGMENT_VERTICES
    this.positions.setXYZ(vertex, from.x, from.y, from.z)
    this.positions.setXYZ(vertex + 1, to.x, to.y, to.z)
    this.colours.setXYZ(vertex, colour.r, colour.g, colour.b)
    this.colours.setXYZ(vertex + 1, colour.r, colour.g, colour.b)
    this.distances.setX(vertex, fromAlong)
    this.distances.setX(vertex + 1, toAlong)
    this.segmentCount++
  }

  /** Doubles the line buffers to hold `needed` segments, keeping the segments this frame already wrote. */
  private growLines(needed: number): void {
    const capacity = grownCapacity(this.segmentCapacity, needed)
    const used = this.segmentCount * SEGMENT_VERTICES
    const geometry = this.root.geometry
    // Frees the GPU copies of the current buffers; the next render uploads the grown ones.
    geometry.dispose()
    this.positions = grownVertices(this.positions, capacity * SEGMENT_VERTICES, used)
    this.colours = grownVertices(this.colours, capacity * SEGMENT_VERTICES, used)
    this.distances = grownVertices(this.distances, capacity * SEGMENT_VERTICES, used)
    geometry.setAttribute('position', this.positions)
    geometry.setAttribute('aColor', this.colours)
    geometry.setAttribute('aDistance', this.distances)
    this.segmentCapacity = capacity
  }
}

function dashFlowOf(reduceEffects: boolean): number {
  return reduceEffects ? DASH_FLOW.reduced : DASH_FLOW.full
}

/** The great-circle angle between two globe directions (radians), stable at every angle; `scratch` takes the cross. */
function arcBetween(a: Vector3, b: Vector3, scratch: Vector3): number {
  return Math.atan2(scratch.crossVectors(a, b).length(), a.dot(b))
}

type StoreArray = Float32Array | Float64Array | Int32Array | Uint8Array

/** A zeroed array like `source` of `length` elements, holding all of `source`. */
function grownArray<T extends StoreArray>(source: T, length: number): T {
  const array = new (source.constructor as new (length: number) => T)(length)
  array.set(source)
  return array
}
